// faro.js — Grafana Faro RUM bootstrap (Real User Monitoring).
//
// Captures web-vitals (LCP/CLS/INP/FCP/TTFB), uncaught errors, unhandled
// rejections, session tracking, page loads, and (via web tracing) W3C trace
// context propagated to our backends so a browser span links to the server
// trace in Tempo.
//
// REUSABLE: this file is framework-agnostic (CRA, Vite, Meteor). To adopt it in
// another React app, copy this file + FaroErrorBoundary.js, install
// `@grafana/faro-react` and `@grafana/faro-web-tracing`, supply config, and call
// initRUM() at the entry point. See observability/REUSABLE-RUM.md.
import {
  initializeFaro,
  getWebInstrumentations,
  ReactIntegration,
} from "@grafana/faro-react";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";

let faro = null;

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Best-effort PII reduction: strip query strings (Keycloak tokens ride in URLs).
// Wrapped so a shape change in Faro never breaks the app — on any error the
// item is sent unchanged.
function redactQueryStrings(item) {
  try {
    const strip = (u) => (typeof u === "string" ? u.split("?")[0] : u);
    if (item?.meta?.page?.url) item.meta.page.url = strip(item.meta.page.url);
    if (item?.payload?.page?.url) item.payload.page.url = strip(item.payload.page.url);
    if (item?.payload?.url) item.payload.url = strip(item.payload.url);
  } catch (_) {
    /* never block telemetry on sanitization */
  }
  return item;
}

/**
 * Initialize RUM. Idempotent. Returns the Faro instance (or null if no
 * collectorUrl is configured, e.g. local dev — RUM is then silently disabled).
 *
 * @param {object}   opts
 * @param {string}   opts.appName            logical app name (e.g. "inventory")
 * @param {string}   opts.environment        "local" | "staging" | "production"
 * @param {string}   opts.collectorUrl       Faro receiver URL (Alloy), e.g. https://faro.ocelotlinks.com/collect
 * @param {string[]} [opts.apiAllowedUrls]   backend origins to attach traceparent to
 * @param {string}   [opts.version]          app version
 * @param {number}   [opts.sessionSampleRate] 0..1 (lower in prod for volume)
 * @param {string}   [opts.appKey]           optional low-grade ingest key
 */
export function initRUM({
  appName,
  environment,
  collectorUrl,
  apiAllowedUrls = [],
  version = "0.0.0",
  sessionSampleRate = 1.0,
  appKey,
} = {}) {
  if (faro) return faro;
  if (!collectorUrl) return null; // not configured (local) → no-op

  try {
    faro = initializeFaro({
      url: appKey ? `${collectorUrl}?apiKey=${encodeURIComponent(appKey)}` : collectorUrl,
      app: { name: appName, version, environment },
      sessionTracking: { enabled: true, samplingRate: sessionSampleRate },
      instrumentations: [
        // web-vitals, errors, unhandledrejection, session, page-load.
        // captureConsole:false → never ship console output (may contain tokens).
        ...getWebInstrumentations({ captureConsole: false }),
        new TracingInstrumentation({
          instrumentationOptions: {
            // Attach W3C traceparent ONLY to our backends (don't leak to 3rd parties).
            propagateTraceHeaderCorsUrls: apiAllowedUrls
              .filter(Boolean)
              .map((u) => new RegExp(escapeRegExp(u))),
          },
        }),
        new ReactIntegration(),
      ],
      beforeSend: redactQueryStrings,
    });
  } catch (e) {
    // RUM must never break the app.
    // eslint-disable-next-line no-console
    console.error("[rum] Faro init failed:", e && e.message);
    faro = null;
  }
  return faro;
}

export function getFaro() {
  return faro;
}

export function reportError(error, context) {
  try {
    faro?.api?.pushError(error, context ? { context } : undefined);
  } catch (_) {}
}

// Identify the user with NON-PII attributes only (Keycloak subject id + role).
// Never pass email/name — those stay out of RUM by design.
export function setRumUser(user) {
  try {
    if (!user) return;
    faro?.api?.setUser({ id: user.id, attributes: { role: user.role || "" } });
  } catch (_) {}
}

export function trackRoute(route) {
  try {
    faro?.api?.pushEvent("route_change", { route });
  } catch (_) {}
}
